<template>
	<div :class="containerClasses">
		<div class="tabs__inner">
			<span
				v-for="(tab, index) in tabs"
				:key="index"
				class="tabs__header__item"
				:class="{ selected: index === activeTab, 'has-count': hasTabCount(tab) }"
				:style="itemStyles"
				@click="tabClick(tab, index)">
				<span class="tabs__label">{{ loading ? "Loading..." : tab.label }}</span>
				<span v-if="!loading && hasTabCount(tab)" class="tabs__count">
					{{ tab.count }}
				</span>
			</span>
		</div>
	</div>
</template>

<script>
const general = require("./../../utils/general");
export default {
	name: "Tabs",
	emits: ["tabChange"],
	props: {
		tabs: {
			type: Array,
			default: () => [],
		},
		colour: {
			type: String,
			default: "brown",
		},
		tabStyle: {
			type: String,
			default: "pill",
		},
		background: {
			type: String,
			default: "#ffffff",
		},
		startTab: {
			type: [Number, String],
			default: 0,
		},
		uniqueName: {
			type: String,
			default: "",
		},
		loading: {
			type: Boolean,
			default: false,
		},
		initClick: {
			type: Boolean,
			default: false,
		},
		noSave: {
			type: Boolean,
			default: false,
		},
		highlightTabValue: {
			type: String,
			default: null,
		},
	},
	data() {
		return {
			activeTab: 0,
		};
	},
	computed: {
		containerClasses() {
			return {
				tabs: true,
				[this.colour]: this.tabStyle === "default",
				loading: this.loading,
				[`style-${this.tabStyle}`]: true,
			};
		},
		itemStyles() {
			if (this.tabStyle === "pill" || this.tabStyle === "pill-rounded" || this.tabStyle === "modal") {
				return {};
			}
			return {
				backgroundColor: this.background,
			};
		},
	},
	methods: {
		hasTabCount(tab) {
			return tab && Object.prototype.hasOwnProperty.call(tab, "count") && tab.count !== null && tab.count !== undefined;
		},
		tabClick(tab, index, force) {
			if (!tab.noSelect) {
				this.activeTab = index;
			}
			if (this.uniqueName && !this.noSave && !tab.noSelect) {
				general.viewsSelected(this.uniqueName, index);
			}
			this.$emit("tabChange", { tab, force });
		},
	},
	created() {
		let st = this.startTab,
			c = true,
			clickIt = this.initClick;
		if (isNaN(st)) {
			st = this.tabs.findIndex((t) => t.value === st);
			st = st || 0;
			c = false;
		}
		this.activeTab = st || 0;
		let tabIndex = this.activeTab;
		if (this.uniqueName && !st && c && !this.noSave) {
			let savedTab = general.viewsSelected(this.uniqueName);
			if (savedTab !== null && !!this.tabs[savedTab] && !this.tabs[savedTab].noSelect) {
				tabIndex = savedTab;
				clickIt = true;
			}
		}
		if (clickIt) {
			setTimeout(
				function () {
					this.tabClick(this.tabs[tabIndex], tabIndex, true);
				}.bind(this),
				20
			);
		}
	},
};
</script>
<style lang="less" scoped>
@import "./../../assets/css/less/_helper.less";
@import "./../../assets/css/less/_settings.less";
.tabs {
	display: flex;
	margin: 0;
	overflow-x: auto;

	&.loading {
		opacity: 0.3;
	}

	.tabs__header__item {
		flex-grow: 0;
		text-align: center;
		cursor: pointer;
		transition: 0.3s;
		font-size: 18px;
		border: 3px solid fade(@guidedRose, 50%);
		border-radius: @radiusLg @radiusLg 0 0;
		border-bottom-width: 2px;
		font-weight: @bold;
		position: relative;
		color: fade(@darkContrast, 50%);

		.tabs__label {
			padding: 5px 15px;
			display: inline-block;
			color: inherit;
			font-size: 14px;
		}

		.tabs__count {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			min-width: 20px;
			height: 20px;
			padding: 0 6px;
			margin-right: 10px;
			border-radius: @radiusPill;
			font-size: 12px;
			font-weight: @bold;
			line-height: 1;
			background: fade(@darkContrast, 10%);
			color: inherit;
		}

		&:nth-child(2) {
			left: -10px;
		}

		&:nth-child(3) {
			left: -20px;
		}

		&:nth-child(4) {
			left: -30px;
		}

		&:hover {
			color: fade(@darkContrast, 75%);
			border-color: fade(@guidedRose, 75%);
		}

		&.selected {
			opacity: 1;
			background-color: @guidedRose !important;
			color: #fff;
			z-index: 2;

			.tabs__count {
				background: fade(#fff, 20%);
			}
		}
	}

	&.green {
		> span {
			border-color: fade(@accentAssistTeal, 50%);

			&:hover {
				border-color: fade(@accentAssistTeal, 75%);
			}

			&.selected {
				background-color: @accentAssistTeal !important;
			}
		}
	}

	&.style-pill,
	&.style-pill-rounded {
		overflow: auto;
		position: relative;
		z-index: 2;

		.tabs__inner {
			background: #fff;
			border-radius: @radiusLg @radiusLg 0 0;
			padding: 6px 6px 0;
			gap: 6px;
			display: flex;
		}

		.tabs__header__item {
			border: none;
			border-radius: @radiusLg;
			color: @guidedRose;
			font-size: 18px;
			font-weight: normal;
			padding: 0;
			display: flex;
			left: 0;
			align-items: center;
			justify-content: center;
			min-height: 20px;
			background: transparent;

			.tabs__label {
				padding: 8px 18px;
				font-size: 0.9rem;
			}

			.tabs__count {
				background: fade(@guidedRose, 70%);
				color: #fff;
			}

			&:hover {
				border: none;
				background: fade(@guidedRose, 5%);
			}

			&.selected {
				background: @guidedRoseBgStrong !important;
				z-index: 1;

				.tabs__count {
					background: fade(@guidedRose, 90%);
				}
			}

			&.has-count {
				.tabs__label {
					padding-right: 8px;
				}
			}
		}
	}
	&.style-pill-rounded {
		.tabs__inner {
			border-radius: @radiusLg;
			padding: 6px;
			background-color: rgba(239, 243, 242, 0.3);
		}
		.tabs__header__item {
			&.selected {
				background: fade(@guidedRose, 20%) !important;
			}
		}
		&.white {
			.tabs__inner {
				background-color: #fff;
			}
			.tabs__header__item {
				&.selected {
					background: @guidedRoseBgStrong !important;
				}
			}
		}
	}

	&.style-modal {
		.tabs__inner {
			display: flex;
			gap: 4px;
			padding: 4px;
			border-radius: @radiusMd;
			background-color: transparent;
		}

		.tabs__header__item {
			left: 0;
			border: none;
			border-radius: @radiusMd;
			font-weight: normal;
			color: fade(@darkContrast, 75%);
			background: transparent;

			.tabs__label {
				padding: 7px 14px;
				font-size: 0.85rem;
			}

			&:hover {
				border: none;
				background: transparent;
				color: fade(@darkContrast, 75%);
			}

			&.selected {
				background: fade(@guidedRose, 16%) !important;
				color: @darkContrast;
			}
		}
	}
}
@media screen and (min-width: 768px) {
	.tabs {
		.tabs__header__item {
			.tabs__label {
				padding: 5px 30px;
				font-size: 1em;
			}
		}
	}
}
@media screen and (min-width: 1200px) {
	.tabs {
		.tabs__header__item {
			.tabs__label {
				padding: 5px 50px;
			}
		}
	}
}

.tabs__attention-arrow {
	position: absolute;
	left: 100%;
	font-size: 10px;
	width: 100px;
	font-weight: bold;
	color: @textLinkTeal;
	animation: tabArrowLeft 0.6s ease-in-out infinite alternate;
}

@keyframes tabArrowLeft {
	from {
		transform: translateX(0);
	}
	to {
		transform: translateX(-5px);
	}
}
</style>
